import { useCallback, useEffect, useMemo, useState } from 'react';
import { i18n } from '@lingui/core';
import { colors, Icon } from '@mindbaz/ui-v2';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { uniqueId } from 'lodash';
import { useNavigate } from 'react-router-dom';

import {
  getCompanyAlerts,
  getComplaintsByISP,
  getDBAlerts,
  getPerfByDB,
  getPerfByISP,
} from 'packages/endpoints/dailyReport';

import {
  Container,
  Flex,
  StyledDataGrid,
  TextCustom,
} from './styledComponents';
import {
  TCard,
  TColorsLevel,
  TDateAutocomplete,
  TGrid,
  TAlert,
  TPerfByISP,
} from './types';
import useDataGrid from './useDataGrid';
import Chart from '../../components/common/Chart';

const useCards = (
  dateUse: TDateAutocomplete,
  selectedDatabase: any,
  appleMPPChecked: boolean
) => {
  const navigate = useNavigate();
  const [isGoneError, setGoneError] = useState(false);

  const typeAlert = useMemo(
    () => ({
      preblock: 'SNPRI_',
      blacklist: 'BL_domain',
      blacklistIp: 'BL_ip',
      domains: 'Delegation',
    }),
    []
  );

  const levelPreblock = useMemo(
    () => ({
      SNPRI_LVL1: 1,
      SNPRI_LVL2: 2,
      SNPRI_LVL3: 3,
      SNPRI_LVL4: 4,
    }),
    []
  );

  const isIPAdress = (input: string) => {
    const regex =
      /\b(?:(?:2(?:[0-4][0-9]|5[0-5])|[0-1]?[0-9]?[0-9])\.){3}(?:(?:2([0-4][0-9]|5[0-5])|[0-1]?[0-9]?[0-9]))\b\.15/i;
    return regex.test(input);
  };

  const { date } = dateUse;

  const { data: perfByDB, refetch: refetchPerfByDB } = useQuery(
    ['getPerfByDB'],
    () => getPerfByDB(date),
    {
      onSuccess: (result) => {
        if (result.response?.status === 410) {
          setGoneError(true);
        }
      },
    }
  );

  const { data: companyAlerts, refetch: refetchCompanyAlert } = useQuery(
    ['getCompanyAlerts'],
    () => getCompanyAlerts(date),
    {
      onSuccess: (result) => {
        if (result.response?.status === 410) {
          setGoneError(true);
        }
      },
    }
  );

  const {
    data: deliverabilityAlertData,
    refetch: refetchDeliverabilityAlertData,
  } = useQuery(['getDBAlerts'], () => getDBAlerts(selectedDatabase.id, date), {
    onSuccess: (result) => {
      if (result.response?.status === 410) {
        setGoneError(true);
      }
    },
  });

  const { data: complaints, refetch: refetchComplaints } = useQuery(
    ['getComplaintsByISP'],
    () => getComplaintsByISP(selectedDatabase.id, date),
    {
      onSuccess: (result) => {
        if (result.response?.status === 410) {
          setGoneError(true);
        }
      },
    }
  );

  const { data: perfByISP, refetch: refetchPerfByISP } = useQuery(
    ['getPerfByISP'],
    () => getPerfByISP(selectedDatabase.id, date),
    {
      onSuccess: (result) => {
        if (result.response?.status === 410) {
          setGoneError(true);
        }
      },
    }
  );

  if (isGoneError) {
    navigate('/gone');
  }

  useEffect(() => {
    refetchPerfByDB();
    refetchCompanyAlert();
    refetchComplaints();
    refetchDeliverabilityAlertData();
    refetchPerfByISP();
  }, [
    date,
    selectedDatabase.id,
    refetchPerfByDB,
    refetchCompanyAlert,
    refetchComplaints,
    refetchDeliverabilityAlertData,
    refetchPerfByISP,
  ]);

  const perfByDBWithId = useMemo(() => {
    return (
      perfByDB.length > 0 &&
      perfByDB.map((perf: TGrid) => {
        return {
          ...perf,
          id: uniqueId(),
          yLabel: perf.siteName,
          openersPct: appleMPPChecked ? perf.openersPct : perf.humanOpenersPct,
        };
      })
    );
  }, [perfByDB, appleMPPChecked]);

  const perfByISPWithId = useMemo(() => {
    return (
      perfByISP.length > 0 &&
      perfByISP.map((perf: TGrid) => {
        return {
          ...perf,
          id: uniqueId(),
          yLabel: perf.faiName,
          openersPct: appleMPPChecked ? perf.openersPct : perf.humanOpenersPct,
          siteName: '',
        };
      })
    );
  }, [perfByISP, appleMPPChecked]);

  const preblockAlert: TAlert[] = useMemo(
    () =>
      companyAlerts.length > 0 &&
      companyAlerts
        .filter(
          (alert: TAlert) =>
            alert && alert.type && alert.type.startsWith(typeAlert.preblock)
        )
        .sort(
          (a: any, b: any) =>
            levelPreblock[b.type as keyof typeof levelPreblock] -
            levelPreblock[a.type as keyof typeof levelPreblock]
        ),
    [companyAlerts, typeAlert.preblock, levelPreblock]
  );

  const blacklistData: TAlert[] = useMemo(
    () =>
      companyAlerts.length > 0 &&
      companyAlerts.filter(
        (alert: TAlert) =>
          alert.type === typeAlert.blacklist ||
          alert.type === typeAlert.blacklistIp
      ),
    [companyAlerts, typeAlert]
  );

  const domainsData: TAlert[] = useMemo(
    () =>
      companyAlerts.length > 0 &&
      companyAlerts.filter((alert: TAlert) => alert.type === typeAlert.domains),
    [companyAlerts, typeAlert.domains]
  );

  const nbPreblock = preblockAlert.length;
  const nbBlacklist = blacklistData.length;
  const nbUndelegated = domainsData.length;
  const nbDeliverabilityAlert = deliverabilityAlertData.length;

  const emailsSentByISP = useMemo(
    () =>
      perfByISPWithId.length > 0 &&
      perfByISPWithId
        .map((perf: TPerfByISP) => {
          return {
            volumeSent: perf.volumeSent,
            WithoutText: perf.volumeSent,
            label: perf.faiName,
            previousData: perf.previousVolumeSent,
          };
        })
        .sort((a: TPerfByISP, b: TPerfByISP) => b.volumeSent - a.volumeSent),
    [perfByISPWithId]
  );

  const dataChartSpam = useMemo(
    () => [
      {
        Orange: complaints[0]?.orangeComplaints,
        LaPoste: complaints[0]?.laposteComplaints,
        SFR: complaints[0]?.sfrComplaints,
        Hotmail: complaints[0]?.hotmailComplaints,
        Yahoo: complaints[0]?.yahooComplaints,
        label: 'Spams Volume',
      },
    ],
    [complaints]
  );

  const dataChartSpamRate = useMemo(
    () => [
      {
        Orange: complaints[0]?.orangeComplaintsRate,
        LaPoste: complaints[0]?.laposteComplaintsRate,
        SFR: complaints[0]?.sfrComplaintsRate,
        Hotmail: complaints[0]?.hotmailComplaintsRate,
        Yahoo: complaints[0]?.yahooComplaintsRate,
        label: 'Spam Rate (in %)',
      },
    ],
    [complaints]
  );

  const colorsChart = useMemo(() => [colors.Bleu700], []);
  const colorsChartSpam = useMemo(
    () => [
      colors.Rouge500,
      colors.Orange400,
      colors.Rouge700,
      colors.Bleu500,
      colors.Bleu700,
      colors.GreyBlack,
    ],
    []
  );

  const dataStacked = useMemo(
    () =>
      perfByISPWithId.length > 0 &&
      perfByISPWithId.map((perf: any) => {
        return {
          unsent: perf.nbAddressUnSent,
          undelivered: perf.nbAddressUnDelivered,
          delivered: perf.nbAddressDelivered,
          label: perf.faiName,
        };
      }),
    [perfByISPWithId]
  );

  const colorsStacked = useMemo(
    () => [colors.Rouge500, colors.Orange500, colors.Bleu700],
    []
  );

  const noResult = useCallback((translation: string, dateOption?: string) => {
    return (
      <Flex className="justify-align-center">
        <Icon color="success">check_circle</Icon>
        <TextCustom className="ml-10" variant="subtitle">
          {i18n._(translation, { date: dateOption })}
        </TextCustom>
      </Flex>
    );
  }, []);

  const colorsLevel: TColorsLevel = useMemo(
    () => ({
      1: 'primary',
      2: 'warning',
      3: 'warning',
      4: 'error',
    }),
    []
  );

  const preblockContent = useCallback(
    (props: TAlert, summary?: boolean) => {
      const flexDirection = summary ? 'flex-column' : 'mb-10 ';
      return (
        <Flex className={`justify-center ${flexDirection}`} key={uniqueId()}>
          {summary ? (
            <TextCustom className="ml-5" variant="h3" weight="bold">
              {i18n._(`snapshot.general.preblockSummary`)} {props.name} (
              {i18n._(`snapshot.general.preblock.level`)}
              {levelPreblock[props.type as keyof typeof levelPreblock]})
            </TextCustom>
          ) : (
            <>
              <TextCustom className="ml-5" variant="h3" weight="bold">
                {props.name}
              </TextCustom>
              <TextCustom
                className="ml-5"
                variant="h3"
                weight="bold"
                color={
                  colorsLevel[
                    levelPreblock[props.type as keyof typeof levelPreblock]
                  ]
                }
              >
                ({i18n._(`snapshot.general.preblock.level`)}
                {levelPreblock[props.type as keyof typeof levelPreblock]})
              </TextCustom>
            </>
          )}
          <TextCustom className="ml-5" variant="body">
            {props.details}
          </TextCustom>
        </Flex>
      );
    },
    [colorsLevel, levelPreblock]
  );

  const blackListContent = useCallback(
    (props: TAlert, summary?: boolean) => {
      const mb10 = summary ? '' : 'mb-10';
      return (
        <Container className={mb10} key={uniqueId()}>
          <TextCustom className="ml-5" variant="h3" weight="bold">
            {summary && i18n._(`snapshot.general.blacklistSummary`)}
            {isIPAdress(props.name) ? `IP ${props.name}` : props.name}
          </TextCustom>
          <TextCustom className="ml-5" variant="body">
            {props.details}
          </TextCustom>
        </Container>
      );
    },

    []
  );

  const domainsContent = useCallback((props: TAlert, summary?: boolean) => {
    const mb10 = summary ? '' : 'mb-10';
    return (
      <Container className={mb10}>
        {summary && (
          <TextCustom className="ml-5" variant="h3" weight="bold">
            {i18n._(`snapshot.general.undelegatedSummary`)}
          </TextCustom>
        )}
        <TextCustom className="ml-5" variant="body">
          {props.name}
        </TextCustom>
      </Container>
    );
  }, []);

  const deliverabilityAlertContent = useCallback(() => {
    return (
      deliverabilityAlertData.length > 0 &&
      deliverabilityAlertData.map((props: TAlert) => {
        if (props.type.startsWith(typeAlert.preblock))
          return (
            <Flex className="align-center mb-5">
              <Icon>warning_amberIcon</Icon>
              {preblockContent(props, true)}
            </Flex>
          );
        if (
          props.type === typeAlert.blacklist ||
          props.type === typeAlert.blacklistIp
        )
          return (
            <Flex className="align-center mb-5">
              <Icon>block</Icon>
              {blackListContent(props, true)}
            </Flex>
          );
        if (props.type === typeAlert.domains)
          return (
            <Flex className="align-center mb-5">
              <Icon>dns_outlined</Icon> {domainsContent(props, true)}
            </Flex>
          );
        return '';
      })
    );
  }, [
    typeAlert,
    deliverabilityAlertData,
    preblockContent,
    blackListContent,
    domainsContent,
  ]);

  const generateContent = useCallback(
    (data: any, content: (props: any) => void) => {
      return data.length > 0 && data.map((props: any) => content(props));
    },
    []
  );

  const getContent = useCallback(
    (noResultTranslation: string, content: any) => {
      return content.length > 0 ? content : noResult(noResultTranslation);
    },
    [noResult]
  );

  const { columns, translations } = useDataGrid(perfByISPWithId);

  const generalCards: TCard[][] = useMemo(
    () => [
      [
        {
          label: i18n._(
            '{nbPreblock, plural, one {# Mindbaz Preblock} other {# Mindbaz Preblocks}}',
            { nbPreblock }
          ),
          icon: (
            <Icon color={nbPreblock ? 'error' : undefined}>
              warning_amberIcon
            </Icon>
          ),
          content: getContent(
            i18n._(`snapshot.general.noPreblock`),
            generateContent(preblockAlert, preblockContent)
          ),
          isScrollable: true,
        },
      ],
      [
        {
          label: i18n._(
            '{nbBlacklist, plural, one {# Blacklist} other {# Blacklists}}',
            { nbBlacklist }
          ),
          icon: <Icon color={nbBlacklist ? 'error' : undefined}>block</Icon>,
          content: getContent(
            i18n._(`snapshot.general.noBlacklist`),
            generateContent(blacklistData, blackListContent)
          ),
          isScrollable: true,
        },
      ],
      [
        {
          label: i18n._(
            '{nbUndelegated, plural, one {# Undelegated domain} other {# Undelegated domains}}',
            { nbUndelegated }
          ),
          icon: (
            <Icon color={nbUndelegated ? 'error' : undefined}>
              dns_outlined
            </Icon>
          ),
          content: getContent(
            i18n._(`snapshot.general.noUndelegated`),
            generateContent(domainsData, domainsContent)
          ),
          isScrollable: true,
        },
      ],
      [
        {
          label: i18n._(`snapshot.general.performance`, {
            nbUndelegated,
          }),
          icon: <Icon>insert_chart_outlined</Icon>,
          content: (
            <StyledDataGrid
              maximumRowActionsDisplayed={3}
              checkboxSelection={false}
              columns={columns}
              rows={perfByDBWithId}
              disableSearchBar
              disableFilters
              translations={translations}
            />
          ),
        },
      ],
    ],
    [
      perfByDBWithId,
      columns,
      nbPreblock,
      nbBlacklist,
      nbUndelegated,
      getContent,
      preblockContent,
      blackListContent,
      domainsContent,
      generateContent,
      preblockAlert,
      blacklistData,
      domainsData,
      translations,
    ]
  );

  const noSpams =
    dataChartSpam[0].Orange === 0 &&
    dataChartSpam[0].LaPoste === 0 &&
    dataChartSpam[0].Hotmail === 0 &&
    dataChartSpam[0].SFR === 0 &&
    dataChartSpam[0].Yahoo === 0;

  const performancesCards: TCard[][] = useMemo(
    () => [
      [
        {
          label: i18n._(`snapshot.performance.email`),
          icon: <Icon>mark_email_read</Icon>,
          content: emailsSentByISP && (
            <Chart
              margin={{
                bottom: 10,
                left: 80,
                right: 120,
                top: 5,
              }}
              layout="horizontal"
              data={emailsSentByISP || []}
              enableGridY={false}
              colors={colorsChart}
              isGrouped
              withLegend={false}
            />
          ),
        },
      ],
      [
        {
          label: i18n._(`snapshot.performance.deliverability`),
          icon: <Icon>mail_outline</Icon>,
          content: dataStacked && dataStacked.length > 0 && (
            <Chart
              margin={{
                bottom: 10,
                left: 80,
                right: 80,
                top: 5,
              }}
              layout="horizontal"
              data={dataStacked}
              colors={colorsStacked}
            />
          ),
        },
      ],
      [
        {
          label: i18n._(
            '{nbDeliverabilityAlert, plural, one {# deliverability alert} other {# deliverability alerts}}',
            { nbDeliverabilityAlert }
          ),
          icon: (
            <Icon color={nbDeliverabilityAlert ? 'error' : undefined}>
              warning_amberIcon
            </Icon>
          ),
          content: getContent(
            i18n._(`snapshot.performance.noDeliverabilityAlert`),
            deliverabilityAlertContent()
          ),
          isScrollable: true,
        },
      ],
      [
        {
          label: i18n._(`snapshot.performance.performance`),
          icon: <Icon>insert_chart_outlined</Icon>,
          content: (
            <StyledDataGrid
              maximumRowActionsDisplayed={3}
              checkboxSelection={false}
              columns={columns}
              rows={perfByISPWithId}
              disableSearchBar
              disableFilters
              translations={translations}
            />
          ),
        },
      ],
      [
        {
          label: i18n._(`snapshot.performance.spamVol`),
          content: noSpams ? (
            noResult(
            i18n._(`snapshot.performance.noResult`),
              dateUse.date
                ? format(new Date(dateUse?.date), 'dd/MM/yyyy')
                : undefined
            )
          ) : (
            <Chart
              margin={{
                bottom: 10,
                left: 40,
                right: 110,
                top: 5,
              }}
              layout="vertical"
              data={dataChartSpam}
              colors={colorsChartSpam}
              groupedWithTooltip
              enableGridY
              isGrouped
              innerPadding={5}
              markers
            />
          ),
        },
        {
          label: i18n._(`snapshot.performance.spamPercent`),
          content: noSpams ? (
            noResult(
            i18n._(`snapshot.performance.noResult`),
              dateUse.date
                ? format(new Date(dateUse?.date), 'dd/MM/yyyy')
                : undefined
            )
          ) : (
            <Chart
              margin={{
                bottom: 10,
                left: 40,
                right: 110,
                top: 5,
              }}
              layout="vertical"
              data={dataChartSpamRate}
              colors={colorsChartSpam}
              groupedWithTooltip
              enableGridY
              isGrouped
              innerPadding={5}
              markers
            />
          ),
        },
      ],
    ],
    [
      emailsSentByISP,
      colorsChart,
      dataStacked,
      colorsStacked,
      nbDeliverabilityAlert,
      getContent,
      deliverabilityAlertContent,
      columns,
      perfByISPWithId,
      dataChartSpam,
      dataChartSpamRate,
      colorsChartSpam,
      noSpams,
      noResult,
      dateUse,
      translations,
    ]
  );

  return {
    generalCards,
    performancesCards,
  };
};

export default useCards;
